import React from "react";
import Container from "@/components/marketing/Container";
import { Newspaper, Target, Send, Link2 } from "lucide-react";
import Seo from "@/components/Seo";
import ServiceHero from "@/components/marketing/service/ServiceHero";
import PositioningSection from "@/components/marketing/service/PositioningSection";
import OutreachTiers from "@/components/marketing/service/OutreachTiers";
import HowItWorksLedger from "@/components/marketing/service/HowItWorksLedger";
import SamplePlacementsSplit from "@/components/marketing/service/SamplePlacementsSplit";
import ServiceFAQ from "@/components/marketing/service/ServiceFAQ";
import CTASection from "@/components/marketing/CTASection";

const OUTREACH_STEPS = [
  { icon: Newspaper, title: "We learn your story", desc: "You brief us, or on Enterprise we craft the angle for you." },
  { icon: Target, title: "We build the target list", desc: "We identify the right journalists by sector, beat, and region." },
  { icon: Send, title: "We pitch and follow up", desc: "Our team pitches your story and works the follow-ups." },
  { icon: Link2, title: "You get the coverage", desc: "We report every piece of earned coverage with live links." },
];

const EARNED_SAMPLES = [
  {
    tag: "Expert Commentary",
    outlet: "PWM — Professional Wealth Management (FT Group)",
    descriptor: "The FT Group's private banking title.",
    title: "Dubai's Asian wealth story enters uncertain chapter",
    url: "https://www.pwmnet.com/content/c7d0d11e-7122-458b-86d6-10dd6000cbfc",
    logo: "",
  },
  {
    tag: "Founder Byline",
    outlet: "Campaign Middle East",
    descriptor: "The Middle East's leading marketing title.",
    title: "What Covid taught businesses about marketing, and why it matters now",
    url: "https://campaignme.com/what-covid-taught-businesses-about-marketing-and-why-it-matters-now/",
    logo: "",
  },
  {
    tag: "Executive Byline",
    outlet: "Profit by Pakistan Today",
    descriptor: "Pakistan's leading business magazine.",
    title: "What Pakistan just got right about innovation and regulation",
    url: "https://profit.pakistantoday.com.pk/2026/04/20/what-pakistan-just-got-right-about-innovation-and-regulation",
    logo: "",
  },
];

const OUTREACH_FAQS = [
  { q: "What is journalist outreach?", a: "Journalist outreach is the practice of pitching your story directly to journalists who cover your sector, with the goal of earning editorial coverage. Our in-house PR team handles targeting, pitching, and follow-up." },
  { q: "How is this different from newswire distribution?", a: "Newswire syndicates your release broadly; journalist outreach pitches your story to individual journalists for earned editorial coverage." },
  { q: "Is coverage guaranteed?", a: "Earned coverage depends on journalists' editorial decisions, so it is never guaranteed. We maximise your chances with expert targeting and pitching. For guaranteed publication, see Guaranteed Placements." },
  { q: "What do I need to provide?", a: "Your content, or on Enterprise we write it for you." },
  { q: "Which tier is right for me?", a: "If you have content ready and want targeted pitching, Basic is a strong start. Pro adds a dedicated strategist who sharpens your angle and manages follow-up. Enterprise is a fully managed PR campaign where we write the content and run everything end to end." },
  { q: "How do you report coverage?", a: "We share live links to every earned piece." },
];

export default function JournalistOutreach() {
  const scrollToTiers = () =>
    document.getElementById("tiers")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="bg-canvas">
      <Seo title="Journalist Outreach | Digital24" />
      <ServiceHero
        eyebrow="JOURNALIST OUTREACH"
        title="Coverage that's earned, not bought."
        subtitle="Real journalists choosing to cover your story. Our in-house PR team does the outreach that makes it happen, from targeting to the pitch."
        primaryCTA={{ label: "View Outreach Tiers", onClick: scrollToTiers }}
        secondaryCTA={{ label: "Talk to Our Team", to: "/contact" }}
      />
      <OutreachTiers />
      <PositioningSection />
      {/* Statement band — earned-coverage differentiator elevated */}
      <section className="bg-[#f1ecf7] border-y border-[#e8e6ef]">
        <Container className="py-14 text-center">
          <p className="font-heading text-[28px] font-bold text-bone leading-tight max-w-3xl mx-auto">
            <span className="text-cobalt">Real journalists</span> choosing to cover your story.
          </p>
        </Container>
      </section>
      <HowItWorksLedger
        steps={OUTREACH_STEPS}
        tag="HOW IT WORKS"
        title="How earned outreach works"
      />
      <SamplePlacementsSplit
        samples={EARNED_SAMPLES}
        tag="PROOF"
        title="Coverage we've earned"
      />
      <ServiceFAQ
        faqs={OUTREACH_FAQS}
        tag="FAQ"
        title="Frequently Asked Questions"
      />
      <CTASection
        eyebrow="GET STARTED"
        headline="Ready to earn your coverage?"
        subtitle="Our PR team pitches your story to the journalists who matter. Pick a tier and get started."
      />
    </div>
  );
}